/**
 * FillerWordToolbar - Top toolbar for filler word removal mode.
 * Compact layout optimized for space efficiency.
 * Follows Libretto's calm, editorial design system.
 */
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Checkbox,
    Divider,
    Typography,
    CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    KeyboardArrowUp as ArrowUpIcon,
    KeyboardArrowDown as ArrowDownIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { FillerWordStore, FillerWordCutMode } from './FillerWordStore';
import { REPEATED_WORD_CATEGORY_ID } from './fillerWord';
const CUT_MODE_LABELS: Record<FillerWordCutMode, string> = {
    'ignore': 'Ignore (Strikethrough)',
    'delete': 'Delete',
    'gap': 'Replace with gap',
    'transcript-only': 'Remove from transcript',
};

interface FillerWordToolbarProps {
    onRemoveCurrent: () => void;
    onRemoveAll: () => void;
    onClose: () => void;
}

const FillerWordToolbar: React.FC<FillerWordToolbarProps> = observer(({
    onRemoveCurrent,
    onRemoveAll,
    onClose,
}) => {
    const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
    const [modeAnchor, setModeAnchor] = useState<null | HTMLElement>(null);

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchor(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchor(null);
    };

    const handleModeClick = (event: React.MouseEvent<HTMLElement>) => {
        setModeAnchor(event.currentTarget);
    };

    const handleModeClose = () => {
        setModeAnchor(null);
    };

    const handleModeSelect = (mode: FillerWordCutMode) => {
        FillerWordStore.setCutMode(mode);
        handleModeClose();
    };

    const handleNavigate = async (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            FillerWordStore.goToPrevious();
        } else {
            FillerWordStore.goToNext();
        }

        await FillerWordStore.revealOccurrenceInTranscript(FillerWordStore.currentOccurrence);
    };

    const categories = FillerWordStore.categoriesWithCounts.filter(category => category.id !== REPEATED_WORD_CATEGORY_ID);
    const repeatedCount = FillerWordStore.categoryCounts.get(REPEATED_WORD_CATEGORY_ID) || 0;
    const isLoading = FillerWordStore.isInitializing || FillerWordStore.isRemoving || FillerWordStore.isAnalyzing;
    const counterText = (() => {
        if (FillerWordStore.removableCount > 0) {
            return `${FillerWordStore.currentIndex + 1} / ${FillerWordStore.removableCount}`;
        }
        if (FillerWordStore.totalCount > 0) {
            return `0 / ${FillerWordStore.totalCount}`;
        }
        return '0 / 0';
    })();

    // Shared checkbox style: white fill, dark checkmark
    const checkboxSx = {
        mr: 1.5,
        p: 0,
        color: 'var(--app-border-muted)',
        '&.Mui-checked': {
            color: 'var(--app-text-primary)',
        },
        '&.MuiCheckbox-indeterminate': {
            color: 'var(--app-text-primary)',
        },
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'var(--app-bg-surface)',
                padding: '14px 14px',
                paddingRight: '24px',
                gap: 1.25,
                minHeight: 56,
            }}
        >
            {/* Filter dropdown - styled as a clear select control */}
            <Box
                data-tour="filler-filter-select"
                onClick={handleFilterClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--app-border-subtle)',
                    backgroundColor: 'var(--app-bg-subtle)',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)', borderColor: 'var(--app-border-muted)' },
                }}
            >
                <Typography sx={{ fontSize: 13, color: 'var(--app-text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {FillerWordStore.allCategoriesSelected ? 'All filler words' : 'Selected'}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--app-text-muted)' }} />
            </Box>

            {/* Separator */}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: 'var(--app-border-subtle)' }} />

            {/* Counter and navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 50, px: 0.5 }}>
                        <CircularProgress size={12} sx={{ color: 'var(--app-text-subtle)' }} />
                        <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)' }}>
                            {FillerWordStore.isInitializing
                                ? '...'
                                : FillerWordStore.isRemoving
                                ? `${FillerWordStore.removalProgress}%`
                                : `${FillerWordStore.analysisProgress}%`
                            }
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 13, color: 'var(--app-text-primary)', fontWeight: 600, minWidth: 40, textAlign: 'center', px: 0.5 }}>
                        {counterText}
                    </Typography>
                )}

                <IconButton
                    size="small"
                    onClick={() => { void handleNavigate('prev'); }}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{
                        padding: '3px',
                        color: 'var(--app-text-muted)',
                        border: '1px solid var(--app-border-subtle)',
                        borderRadius: '5px',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)', borderColor: 'var(--app-border-muted)' },
                        '&.Mui-disabled': { color: 'var(--app-border-muted)', borderColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    <ArrowUpIcon sx={{ fontSize: 16 }} />
                </IconButton>

                <IconButton
                    size="small"
                    onClick={() => { void handleNavigate('next'); }}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{
                        padding: '3px',
                        color: 'var(--app-text-muted)',
                        border: '1px solid var(--app-border-subtle)',
                        borderRadius: '5px',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)', borderColor: 'var(--app-border-muted)' },
                        '&.Mui-disabled': { color: 'var(--app-border-muted)', borderColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    <ArrowDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* Separator */}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: 'var(--app-border-subtle)' }} />

            {/* Cut mode dropdown */}
            <Box
                onClick={handleModeClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: '1px solid var(--app-border-subtle)',
                    backgroundColor: 'var(--app-bg-subtle)',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)', borderColor: 'var(--app-border-muted)' },
                }}
            >
                <Typography sx={{ fontSize: 13, color: 'var(--app-text-primary)', fontWeight: 500 }}>
                    {CUT_MODE_LABELS[FillerWordStore.cutMode].split(' ')[0]}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--app-text-muted)' }} />
            </Box>

            {/* Smooth cuts pill toggle with harsh count below */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box
                    onClick={() => FillerWordStore.toggleAvoidHarshCuts()}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        cursor: 'pointer',
                        padding: '5px 12px',
                        borderRadius: '999px',
                        backgroundColor: FillerWordStore.avoidHarshCuts ? '#EFF6FF' : 'transparent',
                        border: FillerWordStore.avoidHarshCuts ? '1px solid #BFDBFE' : '1px solid var(--app-border-subtle)',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            backgroundColor: FillerWordStore.avoidHarshCuts ? '#DBEAFE' : 'var(--app-bg-subtle)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: FillerWordStore.avoidHarshCuts ? '#2B6BFD' : 'var(--app-border-muted)',
                            transition: 'background-color 0.15s ease',
                        }}
                    />
                    <Typography sx={{
                        fontSize: 12,
                        color: FillerWordStore.avoidHarshCuts ? '#1D4ED8' : 'var(--app-text-muted)',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                    }}>
                        Smooth cuts
                    </Typography>
                </Box>

                {/* Harsh cut warning - positioned below, doesn't affect layout */}
                {FillerWordStore.harshCount > 0 && FillerWordStore.avoidHarshCuts && (
                    <Typography
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: '4px',
                            fontSize: 11,
                            color: '#DC2626',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {FillerWordStore.harshCount} harsh
                    </Typography>
                )}
            </Box>

            {/* Action buttons - stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                <Button
                    data-tour="filler-remove-current"
                    variant="text"
                    size="small"
                    onClick={onRemoveCurrent}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{
                        color: 'var(--app-text-primary)',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--app-border-subtle)',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: 'var(--app-bg-subtle)',
                            borderColor: 'var(--app-border-muted)',
                        },
                        '&.Mui-disabled': { color: 'var(--app-border-muted)', borderColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    Remove
                </Button>
                <Button
                    data-tour="filler-remove-all"
                    variant="text"
                    size="small"
                    onClick={onRemoveAll}
                    disabled={FillerWordStore.removableCount === 0 || isLoading}
                    sx={{
                        color: 'var(--app-bg-surface)',
                        backgroundColor: 'var(--app-text-primary)',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: 'var(--app-text-primary)',
                            opacity: 0.85,
                        },
                        '&.Mui-disabled': { color: 'var(--app-text-subtle)', backgroundColor: 'var(--app-bg-subtle)' },
                    }}
                >
                    Remove all ({FillerWordStore.removableCount})
                </Button>
            </Box>

            {/* Close button - top right */}
            <IconButton
                size="small"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    padding: '4px',
                    color: 'var(--app-text-subtle)',
                    '&:hover': { backgroundColor: 'var(--app-bg-subtle)', color: 'var(--app-text-primary)' },
                }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>

            {/* Filter menu */}
            <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={handleFilterClose}
                PaperProps={{
                    sx: {
                        minWidth: 220,
                        maxHeight: 450,
                        overflowY: 'auto',
                        backgroundColor: 'var(--app-bg-surface)',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'var(--app-border-muted) transparent',
                        '&::-webkit-scrollbar': {
                            width: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'var(--app-border-muted)',
                            borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: 'var(--app-text-subtle)',
                        },
                        boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                        borderRadius: '10px',
                        border: '1px solid var(--app-border-subtle)',
                        mt: 1,
                    }
                }}
            >
                {/* Select all */}
                <MenuItem
                    onClick={() => {
                        if (FillerWordStore.allCategoriesSelected) {
                            FillerWordStore.deselectAllCategories();
                        } else {
                            FillerWordStore.selectAllCategories();
                        }
                    }}
                    sx={{ py: 1.25, px: 2 }}
                >
                    <Checkbox
                        size="small"
                        checked={FillerWordStore.allCategoriesSelected}
                        indeterminate={FillerWordStore.someCategoriesSelected}
                        sx={checkboxSx}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--app-text-primary)' }}>Select all</Typography>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* Repeated words */}
                <MenuItem
                    onClick={() => FillerWordStore.toggleRepeatedWords()}
                    sx={{ py: 1.25, px: 2 }}
                >
                    <Checkbox
                        size="small"
                        checked={FillerWordStore.includeRepeatedWords}
                        sx={checkboxSx}
                    />
                    <Typography sx={{ fontSize: 13, flex: 1, color: 'var(--app-text-primary)' }}>Repeated words</Typography>
                    {repeatedCount > 0 && (
                        <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)', ml: 2 }}>
                            {repeatedCount}
                        </Typography>
                    )}
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* Individual categories */}
                {categories.map(category => (
                    <MenuItem
                        key={category.id}
                        onClick={() => FillerWordStore.toggleCategory(category.id)}
                        sx={{ py: 1, px: 2, pl: 4 }}
                    >
                        <Checkbox
                            size="small"
                            checked={category.enabled}
                            sx={checkboxSx}
                        />
                        <Typography sx={{ fontSize: 13, flex: 1, color: 'var(--app-text-primary)' }}>{category.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--app-text-subtle)', ml: 2 }}>
                            {category.count}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>

            {/* Cut mode menu */}
            <Menu
                anchorEl={modeAnchor}
                open={Boolean(modeAnchor)}
                onClose={handleModeClose}
                PaperProps={{
                    sx: {
                        minWidth: 200,
                        backgroundColor: 'var(--app-bg-surface)',
                        boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                        borderRadius: '10px',
                        border: '1px solid var(--app-border-subtle)',
                        mt: 1,
                    }
                }}
            >
                {(Object.keys(CUT_MODE_LABELS) as FillerWordCutMode[]).map(mode => (
                    <MenuItem
                        key={mode}
                        onClick={() => handleModeSelect(mode)}
                        selected={FillerWordStore.cutMode === mode}
                        sx={{
                            py: 1.25,
                            px: 2,
                            fontSize: 13,
                            color: 'var(--app-text-primary)',
                            '&.Mui-selected': {
                                backgroundColor: 'var(--app-bg-subtle)',
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: 'var(--app-border-subtle)',
                            },
                        }}
                    >
                        {CUT_MODE_LABELS[mode]}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
});

export default FillerWordToolbar;
